<template>
	<loading-el	v-if="loading || listLoading"></loading-el>
	<template v-if="!isDetailsInterface && !isMediaUploadInterface">
		<h2 class="popup-headline">{{ t('popup.media.title') }}</h2>
		<alert-message
			v-if="alert.isShown"
			:alert="alert"
			@close-alert="alertHide"
			class="popup-alert"
		></alert-message>
		<div ref="scrollArea" class="assets-list">
			<label class="assets-item assets-item--upload">
				<input @input="handleFileSelected" multiple type="file" :accept="MEDIA_ACCEPT_TYPES ">
				<div class="assets-item__figure  assets-item__figure--create">
					<i class="fas fa-plus"></i>
				</div>
				<div class="assets-item__title">{{ t('popup.media.button.upload') }}</div>
			</label>
			<div
				v-for="media in assets"
				:key="media.key"
			>
				<div class="assets-item">
					<div class="assets-item__figure external-item">
<!--						<img :src="`${media.variants.default[0] ? hostURL + media.variants.default[0] : mediaURL + media.key}`" :alt="media.filename">-->
						<img v-if="media.mime_type.includes('image')" :src="getThumbnailURL(media)" loading="lazy" alt="">
						<canvas v-else-if="isLottieMedia(media)" :ref="el => setLottieCanvas(media.key, el)" class="lottie-preview" style="width:100%;height:100%"></canvas>
						<i v-else-if="media.mime_type.includes('video')" class="fas fa-film video-icon"></i>
						<i v-else class="fas fa-file-alt video-icon"></i>
            <div v-if="media.nodes?.length" class="external-item__details">
              <div class="external-item__count">{{ media.nodes?.length }}</div>
              <div class="external-item__nodes">
                <i class="fas fa-link"></i>
                <span>{{ t('popup.content.label.node') }}: </span>
                <span v-html="compressArrayToString(media.nodes)"></span>
              </div>
            </div>
						<div @click.stop="isMediaChoosing ? getMediaURL(media) : null" class="assets-item__layer">
							<div v-if="!isMediaChoosing" class="button-wrapper button-wrapper--assets">
								<div
									@click="getMediaURL(media)"
									class="button button--assets bg--dusky-rose"><i class="fas fa-link"></i></div>
								<div
									v-if="!isMediaChoosing"
									@click="getMediaDetails(media)"
									class="button button--assets"
								><i class="fas fa-info"></i></div>
								<div
									v-if="!isMediaChoosing"
									@click="deleteMedia(media)"
									class="button button--assets bg--black"
								><i class="fas fa-trash"></i></div>
							</div>
						</div>
						<div v-if="media.check_translated !== null" class="assets-item__label">
							<i
								:class="media.check_translated ? 'bg--blue-grey' : 'bg--golden'"
								class="fas fa-language"
							></i>
						</div>
					</div>
					<div class="assets-item__title">
						<span>{{ media.filename }}</span>
						<div class="assets-item__tooltip">{{ media.filename }}</div>
					</div>
				</div>
			</div>
		</div>
		<list-more
			:has-more="hasMore"
			:loading="loadingMore"
			:total="total"
			@load="loadMore"
		></list-more>
	</template>
	<media-details
		v-else-if="isDetailsInterface"
		:media="mediaCurrent"
		@close-details="detailsClose"
		@media-update="mediaUpdate"
	></media-details>
	<media-upload
		v-else-if="isMediaUploadInterface"
		:files="uploadedFiles"
		@close-upload="uploadClose"
		@insert-media="insertMedia"
	></media-upload>
	<popup-dialog :popup-active="popupActive" class="popup-window">
		<div class="confirm">
			<i @click="deleteCancel" class="fas fa-times confirm-close"></i>
			<div class="confirm__content">
				{{ t('popup.confirm.warning', {action: t('popup.confirm.actions.deleteAsset')}) }} {{ t('popup.confirm.cannotUndone') }}<br>
				{{ t('popup.confirm.youSure') }}</div>
			<div class="button-wrapper confirm__button-wrapper">
				<div @click="deleteCancel" class="button confirm__button bg--blue-grey color--black">{{t('popup.confirm.stopMe')}}</div>
				<div @click="deleteConfirm" class="button confirm__button">
					<i class="fas fa-trash mr--9"></i>
					<span>{{t('popup.confirm.sure')}}</span>
				</div>
			</div>
		</div>
	</popup-dialog>
</template>

<script setup>
import {useStore} from 'vuex';
import {ref, computed, provide, inject, onMounted, onUnmounted, watch, nextTick} from 'vue';
import ApiService from '@/utils/ApiService';
import AlertMessage from '@/components/ui/alert/AlertMessage.vue';
import useAlert from '@/utils/Alert';
import {ALERT_TYPES} from '@/models/ALERT_TYPES';
import useLoading from '@/utils/Loading';
import LoadingEl from '@/components/ui/loading/LoadingEl.vue';
import MediaDetails from '@/components/editor-options/settings/popup/content/media/MediaDetails';
import {SE_SYNC_TYPES} from '@/models/SE_SYNC_TYPES';
import {useI18n} from 'vue-i18n';
import usePopup from '@/utils/Popup';
import PopupDialog from '@/components/ui/popup/PopupDialog';
import {DASHBOARD_OPTIONS} from '@/models/DASHBOARD_OPTIONS';
import MediaUpload from '@/components/editor-options/settings/popup/content/media/MediaUpload';
import ListMore from '@/components/overview/ListMore.vue';
import useInfiniteList from '@/utils/useInfiniteList';
import useTranslations from "@/utils/language/useTranslations";
import {DotLottie} from '@lottiefiles/dotlottie-web';

const emit = defineEmits(["chosenMedia"])

// Composables
const {t} = useI18n();
const { alert, alertHide, alertShow, alertCreate } = useAlert();
const { loading, loadingShow, loadingHide } = useLoading();
const { popupActive, popupOpen, popupClose } = usePopup();
const store = useStore();

// Urls data
const currentMap = computed(() => store.getters['maps/getCurrentMap'])
const assetsURL = computed(() => store.getters['mediaAssets/getAssetsURL']);
const mediaURL = computed(() => `${import.meta.env.VITE_ROOT_API}${assetsURL.value}`);
const hostURL = import.meta.env.VITE_ROOT_HOST;

function getThumbnailURL(media) {
  if(!media.key?.endsWith('.gif')) {
    const variants = media.variants;
    const variant = variants?.xsmall?.[0] || variants?.small?.[0] || variants?.default?.[0];
    if(variant) return `${hostURL}${variant}`;
  }
  return `${mediaURL.value}${media.key}`;
}

// Popup states
const isMediaChoosing = computed(() => store.getters["settingsPopup/isMediaChoosing"]);
const isImageVideoMode = computed(() => store.getters["settingsPopup/getImageVideoMode"]);
const imageVideoModeSet = () => {store.dispatch('settingsPopup/imageVideoModeSet', false)}

// Component state.  The order and the image/video restriction are the
// server's: sorting or discarding within the pages that happen to be loaded
// would only order part of the library and empty pages of what it drops.
const scrollArea = ref(null);
const {
  items: assets,
  total,
  hasMore,
  loading: listLoading,
  loadingMore,
  load: loadAssets,
  error: listError,
  loadMore,
  addItem: addAsset,
  removeItem: removeAsset,
  replaceItem: replaceAsset,
  trackScroll,
  dispose: disposeList
} = useInfiniteList({
  url: assetsURL,
  params: () => ({
    map: currentMap.value.id,
    type: !isMediaChoosing.value && isImageVideoMode.value
      ? ['image', 'video']
      : undefined
  }),
  transform: results => findMediaUsage(nodesOriginal.value, results)
});

function isLottieMedia(media) {
  return /\.(lot|lottie|lottie\.json|json)$/i.test(media.filename);
}

const lottieCanvases = ref({});
const lottieInstances = {};
const lottieBlobUrls = {};
function setLottieCanvas(key, el) {
  if(el){
    lottieCanvases.value[key] = el;
  } else {
    delete lottieCanvases.value[key];
    if(lottieInstances[key]){
      lottieInstances[key].destroy();
      delete lottieInstances[key];
    }
  }
}
async function initLottiePreview(media) {
  const canvas = lottieCanvases.value[media.key];
  if(!canvas || lottieInstances[media.key]) return;
  try {
    if(!lottieBlobUrls[media.key]){
      const response = await ApiService.get(`${assetsURL.value}${media.key}`, {
        responseType: 'blob',
        headers: {'Accept': 'application/octet-stream'}
      });
      lottieBlobUrls[media.key] = URL.createObjectURL(response.data);
    }
    lottieInstances[media.key] = new DotLottie({
      canvas,
      src: lottieBlobUrls[media.key],
      autoplay: true,
      loop: true
    });
  } catch(e) {
    console.error('Failed to load lottie preview:', e);
  }
}

const mediaCurrent = ref(null);
const MEDIA_ACCEPT_TYPES  = '.png, .jpg, .jpeg, .gif, .svg, .mov, .mp4, .mpeg, .webm, .mkv, .mp3, .opus, .ogg, .ppt, .pdf, .zip, .doc, .docx, .txt, .lot, .lottie, .lottie.json, .json';
const uploadedFiles = ref(null);

// Event handlers
function handleFileSelected(event){
  if (!event?.target) {
    console.error("Event or event.target is undefined");
    return;
  }
  const input = event.target;
  if (!input || input.tagName !== 'INPUT') {
    console.error("Invalid event target");
    return;
  }
  uploadedFiles.value = event.target.files;
  isMediaUploadInterface.value = true;
}
function indexExistingFilename(name){
  return assets.value.findIndex(file => file.filename === name);
}

function detailsClose(){
  isDetailsInterface.value = false;
  mediaCurrent.value = null;
}
function uploadClose(){
  isMediaUploadInterface.value = false;
  uploadedFiles.value = null;
}

// Media operation
function insertMedia(gallery){
  gallery.forEach(media => {
    if(indexExistingFilename(media.filename) > -1){
      replaceAsset(file => file.filename === media.filename, media);
    } else addAsset(media);
  })
}

const deletedMediaKey = ref(null);
async function deleteConfirm(){
  loadingShow();
  popupClose();
  try{
    const mediaKey = deletedMediaKey.value;
    await ApiService.delete(`${assetsURL.value}${mediaKey}`);
    // Before deleting, clear entities from traces of file usage
    await designCheckDeletedMedia(mediaKey);
    await nodesCheckDeletedMedia(mediaKey);
    await mapSettingsCheckDeletedMedia(mediaKey);
    await userCheckDeletedMedia(mediaKey);
    // SE send event
    const editsData = {
      type: SE_SYNC_TYPES.MEDIA_ASSET_REMOVED,
      data: {mediaKey}
    }
    await store.dispatch('simultaneousEditing/syncEdits', editsData);
    removeAsset(asset => asset.key === mediaKey);
    loadingHide();
  }
  catch(err) {
    loadingHide();
  }
}
function deleteCancel(){
  deletedMediaKey.value = null;
  popupClose();
}
function deleteMedia(media){
  deletedMediaKey.value = media.key;
  popupOpen();
}
async function nodesCheckDeletedMedia(mediaKey){
  await store.dispatch('nodes/checkDeletedMedia', mediaKey);
}
async function designCheckDeletedMedia(mediaKey){
  await store.dispatch('design/checkDeletedMedia', mediaKey);
}
async function mapSettingsCheckDeletedMedia(mediaKey){
  await store.dispatch('mapSettings/checkDeletedMedia', mediaKey);
}
async function userCheckDeletedMedia(mediaKey){
  await store.dispatch('user/checkDeletedMedia', mediaKey);
}

const {syncTranslatedEdits} = useTranslations();
function getTranslatedTotal(){
  const result = {};

  for(const media of assets.value){

    const lang = media.lang;
    for(const id of Object.keys(lang)){
      // add id to result if not exist which contains {translated:true} status by default
      if(!Object.hasOwn(result, id)){
        result[id] = {translated: true};
      }
      // if any of the node language is not translated, set translated status to false
      if(!lang[id].translated && result[id].translated){
        result[id].translated = false;
      }
    }
  }
  return result;
}

async function mediaUpdate(settings){
  const key = settings.key;

  delete settings.duration;
  delete settings.filename;
  delete settings.height;
  delete settings.width;
  delete settings.mime_type;
  delete settings.key;
  delete settings.is_template;
  delete settings.generated_variant;
  delete settings.generated;
  delete settings.check_translated;

  // todo temporary
  delete settings.variants

  const request = adaptorBackMedia(settings);

  loadingShow();
  try{
    const {data} = await ApiService.patch(`${assetsURL.value}${key}`, request);
    if( findMediaIndex(key) > -1) {
      Object.assign(assets.value[findMediaIndex(key)], data);
    }

    // update translated status in Overview Translations
    const syncData = getTranslatedTotal();
    syncTranslatedEdits({data: syncData, section: DASHBOARD_OPTIONS.ORGANIZE.GROUPS['1'].MANAGE_ASSETS.VALUE})

    detailsClose();
    loadingHide();
    alertCreate({type: ALERT_TYPES.SUCCESS, message: t('popup.media.message.update')});
    alertShow();
  } catch (err){
    detailsClose();
    alertCreate({type: ALERT_TYPES.ERROR, message: err.response.data.detail});
    alertShow();
    loadingHide();
  }
}
function findMediaIndex(key){
  return assets.value.findIndex(item => item.key === key);
}

async function getMediaURL(media){
  /*const keyImage = media.variants.default[0] ? `${hostURL}${media.variants.default[0]}` : `${mediaURL.value}${media.key}`;
  const keyVideo = media.variants.default[1] ? `${hostURL}${media.variants.default[1]}` : `${mediaURL.value}${media.key}`;
  const chosenMediaURL = media.mime_type.includes('video') ? keyVideo : keyImage;*/
  const chosenMediaURL = `${mediaURL.value}${media.key}`;
  // const chosenMediaURL = `${hostURL}${key}`
  if(isMediaChoosing.value){
    emit("chosenMedia", {url: chosenMediaURL, name: media.filename, type: media.mime_type});
  } else {
    await navigator.clipboard.writeText(chosenMediaURL);
    alertCreate({type: ALERT_TYPES.SUCCESS, message: t('popup.media.message.paste')});
    alertShow();
    setTimeout(() => alertHide(), 2000);
  }
}

const isDetailsInterface = ref(false);
const isMediaUploadInterface = ref(false);
async function getMediaDetails(media){
  mediaCurrent.value = adaptorMedia(media);
  isDetailsInterface.value = true;
}

// Utils
function adaptorMedia(media){
  const data = Object.assign({}, media);
  for(const [key, value] of Object.entries(data)){
    if(value === null){
      data[key] = '';
    }
  }
  return data;
}
function adaptorBackMedia(media){
  const data = Object.assign({}, media);
  for(const [key, value] of Object.entries(data)){
    if(value === ''){
      data[key] = null;
    }
  }
  return data;
}

const nodesOriginal = computed(() => store.getters['nodes/getRawNodes']);
async function fetchAssets(){
  loadingShow();
  await loadAssets();
  if(listError.value){
    const message = listError.value.response?.data?.detail
      ? listError.value.response.data.detail
      : t('popup.media.message.errorLoading');
    alertCreate({type: ALERT_TYPES.ERROR, message: message});
    alertShow();
  }
  loadingHide();
}

function findMediaUsage(nodes, assets) {
  const result = [];

  assets.forEach(asset => {
    const filename = asset.filename;

    nodes.forEach((node, id) => {
      if(node.design.media?.endsWith(filename) || node.design.media_url?.endsWith(filename)){
        !asset.nodes ? asset.nodes = [node.id] : asset.nodes.push(node.id)
      }
      const contentFields = ['content', 'marginal_content_1', 'marginal_content_2']

      for(const lang of Object.values(node.content.lang)){
        contentFields.forEach(field => {
          if (!lang[field]) return; // Skip if the field is empty or undefined
          const parser = new DOMParser();
          // Parse the HTML string into a Document object
          const doc = parser.parseFromString(lang[field], 'text/html');
          const targetImages = doc.querySelectorAll(`img[src$="${filename}"]`);
          targetImages.forEach(() => {
            !asset.nodes ? asset.nodes = [node.id] : asset.nodes.push(node.id)
          })
          const targetLotties = doc.querySelectorAll(`[data-lottie-src$="${filename}"]`);
          targetLotties.forEach(() => {
            !asset.nodes ? asset.nodes = [node.id] : asset.nodes.push(node.id)
          })
        })
      }
    });

    result.push(asset);
  })

  return result;
}
function compressArrayToString(arr) {
  if (arr.length === 0) return '';

  let result = [];
  let current = arr[0];
  let count = 1;

  for (let i = 1; i < arr.length; i++) {
    if (arr[i] === current) {
      count++;
    } else {
      if (count > 1) {
        result.push(`${current}<sup>${count}</sup>`);
      } else {
        result.push(current);
      }
      current = arr[i];
      count = 1;
    }
  }

  if (count > 1) {
    result.push(`${current}<sup>${count}</sup>`);
  } else {
    result.push(current);
  }

  return result.join(', ');
}

// SYNC SECTION LOCK
const BLOCKED_TYPE = DASHBOARD_OPTIONS.ORGANIZE.GROUPS["1"].MANAGE_ASSETS.BLOCKED_TYPE;
onMounted(() => {
  store.dispatch("simultaneousEditing/startEditingOption", BLOCKED_TYPE);
  trackScroll(scrollArea.value);
});
// The picker's mode decides which media types the server is asked for, so a
// change to it starts the list over rather than re-sifting what is loaded.
watch([isMediaChoosing, isImageVideoMode], () => loadAssets());
watch(scrollArea, element => trackScroll(element));
onUnmounted(() => {
  store.dispatch("simultaneousEditing/stopEditingOption", BLOCKED_TYPE);
  disposeList();
  Object.values(lottieInstances).forEach(inst => inst.destroy());
  Object.values(lottieBlobUrls).forEach(url => URL.revokeObjectURL(url));
})

// PROCESS
fetchAssets();

function initAllLottiePreviews() {
  nextTick(() => {
    const lottieAssets = (assets.value || []).filter(isLottieMedia);
    lottieAssets.forEach(media => initLottiePreview(media));
  });
}
watch(assets, initAllLottiePreviews);
watch(isDetailsInterface, (val) => {
  if(!val) initAllLottiePreviews();
});
watch(isMediaUploadInterface, (val) => {
  if(!val) initAllLottiePreviews();
});

</script>

<style lang="scss">
@use "@/scss/items/assets/assets-list";
@use "@/scss/items/assets/assets-item";
@use "@/scss/items/external/external-item";
@use "@/scss/sections/overview/list-more";
</style>
